// src/pages/blog/BlogCategory.js
import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Search,
  Calendar,
  Eye,
  ChevronRight,
  AlertCircle,
  ArrowLeft,
} from "lucide-react";
import { Helmet } from "react-helmet";
import { useConfig } from "../../context/ConfigContext";

const BlogCategory = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { config } = useConfig(); // Get config from context
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState(null);
  const [categories, setCategories] = useState(null);
  const [posts, setPosts] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    try {
      // Set the document title using the site name from config
      document.title = "Blog | " + config?.siteName;
    } catch (err) {
      console.error("Error fetching page:", err);
    }
  }, [config?.siteName]); // Add config.siteName to the dependency array

  useEffect(() => {
    fetchCategory();
  }, [slug]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (category) {
      fetchPosts(currentPage);
    }
  }, [category, currentPage]);

  // Skeleton loader for loading state
  const SkeletonCard = () => (
    <div className="bg-white rounded-lg overflow-hidden shadow-md animate-pulse">
      <div className="h-48 bg-gray-200"></div>
      <div className="p-5">
        <div className="flex gap-2 mb-3">
          <div className="h-6 w-16 bg-gray-200 rounded-full"></div>
          <div className="h-6 w-20 bg-gray-200 rounded-full"></div>
        </div>
        <div className="h-6 bg-gray-200 rounded mb-3"></div>
        <div className="h-4 bg-gray-200 rounded mb-2"></div>
        <div className="h-4 bg-gray-200 rounded mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
        <div className="h-6 w-24 bg-gray-200 rounded"></div>
      </div>
    </div>
  );

  const fetchCategory = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/blog/categories/${slug}`);

      if (!res.ok) {
        if (res.status === 404) {
          throw new Error("Category not found");
        }
        throw new Error("Failed to fetch category");
      }

      const data = await res.json();
      setCategory(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching category:", err);
      setError(err.message || "Failed to load the category");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/blog/categories`);

      if (!res.ok) {
        if (res.status === 404) {
          throw new Error("Category not found");
        }
        throw new Error("Failed to fetch category");
      }

      const data = await res.json();
      setCategories(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching category:", err);
      setError(err.message || "Failed to load the category");
    } finally {
      setLoading(false);
    }
  };

  const fetchPosts = async (page) => {
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams({
        page,
        limit: 6,
        sort: "newest",
      });

      const res = await fetch(
        `/api/blog/posts?category=${slug}&${params.toString()}`
      );

      if (!res.ok) throw new Error("Failed to fetch posts");

      const data = await res.json();
      setPosts(data.posts);
      setTotalPages(data.totalPages);

      setError(null);
    } catch (err) {
      console.error("Error fetching posts:", err);
      setError("Failed to load posts. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Calculate read time in minutes
  const getReadTime = (content) => {
    const wordsPerMinute = 200;
    const wordCount = content.split(/\s+/).length;
    return Math.ceil(wordCount / wordsPerMinute);
  };

  if (loading && !category) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 py-12">
          <div className="animate-pulse">
            <div className="h-12 bg-gray-200 rounded w-1/3 mx-auto mb-8"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto mb-12"></div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !category) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <p>{error || "Category not found"}</p>
            <button
              onClick={() => navigate("/blog")}
              className="font-bold hover:underline mt-2 inline-block"
              style={{ color: "#EF4444" }}
            >
              Return to Blog
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{category.name + " Category | " + config.siteName}</title>
        <meta
          name="description"
          content={`Articles in the ${category.name} category - ${
            config?.siteName || "BagBox"
          } Blog`}
        />
      </Helmet>

      {/* Category Header */}
      <div
        className="text-white py-16"
        style={{
          background: `linear-gradient(to right, ${
            config.colors.primary || "#3B82F6"
          }, ${config.colors.accent || "#4F46E5"})`,
        }}
      >
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center">
            {category.name}
          </h1>
          {category.description && (
            <p className="text-xl text-center max-w-2xl mx-auto">
              {category.description}
            </p>
          )}

          {/* Breadcrumbs */}
          <div className="flex justify-center mt-6 text-blue-100">
            <nav className="flex items-center">
              <Link to="/" className="hover:text-white">
                Home
              </Link>
              <span className="mx-2">/</span>
              <Link to="/blog" className="hover:text-white">
                Blog
              </Link>
              <span className="mx-2">/</span>
              <span>Category</span>
              <span className="mx-2">/</span>
              <span className="font-medium">{category.name}</span>
            </nav>
          </div>
        </div>
      </div>

      {/* Main content container */}
      <div className="container mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Posts section - takes up 3/4 of the width on large screens */}
          <div className="w-full lg:w-3/4">
            {error && (
              <div className="bg-red-100 text-red-800 p-4 rounded-lg mb-8">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="h-5 w-5" />
                  <span>{error}</span>
                </div>
              </div>
            )}

            {/* Posts Grid */}
            {loading && !posts.length ? (
              <div className="text-center py-20">
                <div
                  className="spinner mx-auto mb-4"
                  style={{ borderTopColor: config.colors.primary }}
                ></div>
                <p className="text-gray-600">Loading posts...</p>
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-20 bg-gray-50 rounded-lg">
                <p className="text-gray-500 mb-4">
                  No posts found in this category
                </p>
                <Link
                  to="/blog"
                  className="hover:text-blue-800"
                  style={{ color: config.colors.primary }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.color = `${config.colors.primary}DD`;
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.color = config.colors.primary;
                  }}
                >
                  Back to Blog
                </Link>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {posts.map((post) => (
                    <div
                      key={post._id}
                      className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
                    >
                      <Link to={`/blog/${post.slug}`}>
                        <div className="h-48 bg-gray-200">
                          {post.coverImage ? (
                            <img
                              src={post.coverImage}
                              alt={post.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <span className="text-gray-400">No image</span>
                            </div>
                          )}
                        </div>
                      </Link>
                      <div className="p-6">
                        <div className="flex items-center mb-2 text-sm text-gray-500">
                          <Calendar
                            className="h-4 w-4 mr-1"
                            style={{ color: `${config.colors.primary}AA` }}
                          />
                          {formatDate(post.publishedAt || post.createdAt)}
                          <span className="mx-2">•</span>
                          <Eye
                            className="h-4 w-4 mr-1"
                            style={{ color: `${config.colors.primary}AA` }}
                          />
                          {post.viewCount} views
                        </div>
                        <h3 className="text-xl font-bold mb-2 line-clamp-2">
                          <Link
                            to={`/blog/${post.slug}`}
                            style={{
                              color: config.colors.textPrimary || "#111827",
                              ":hover": { color: config.colors.primary },
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.color =
                                config.colors.primary;
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.color =
                                config.colors.textPrimary || "#111827";
                            }}
                          >
                            {post.title}
                          </Link>
                        </h3>
                        <p className="text-gray-600 mb-4 line-clamp-3">
                          {post.excerpt}
                        </p>
                        <Link
                          to={`/blog/${post.slug}`}
                          className="flex items-center text-sm font-medium"
                          style={{ color: config.colors.primary }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.color = `${config.colors.primary}DD`;
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.color = config.colors.primary;
                          }}
                        >
                          Read More
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-10 flex justify-center">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() =>
                          setCurrentPage((prev) => Math.max(prev - 1, 1))
                        }
                        disabled={currentPage === 1}
                        className={`px-3 py-1 rounded ${
                          currentPage === 1
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                        }`}
                      >
                        Previous
                      </button>

                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (page) => (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`w-8 h-8 rounded flex items-center justify-center ${
                              currentPage === page
                                ? "text-white"
                                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                            style={{
                              backgroundColor:
                                currentPage === page
                                  ? config.colors.primary
                                  : "",
                            }}
                          >
                            {page}
                          </button>
                        )
                      )}

                      <button
                        onClick={() =>
                          setCurrentPage((prev) =>
                            Math.min(prev + 1, totalPages)
                          )
                        }
                        disabled={currentPage === totalPages}
                        className={`px-3 py-1 rounded ${
                          currentPage === totalPages
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                        }`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Sidebar - takes up 1/4 of the width on large screens */}
          <div className="w-full lg:w-1/4 mt-8 lg:mt-0">
            {/* Categories */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3
                className="text-xl font-bold mb-4"
                style={{ color: config.colors.textPrimary || "#111827" }}
              >
                Categories
              </h3>
              <ul className="space-y-2">
                {categories &&
                  categories.map((cat) => (
                    <li key={cat.id} className="group">
                      <Link
                        to={`/blog/category/${cat.slug}`}
                        className={`flex items-center group-hover:translate-x-1 transition-transform`}
                        style={{
                          color:
                            cat.slug === slug
                              ? config.colors.primary
                              : config.colors.textPrimary || "#6B7280",
                          fontWeight: cat.slug === slug ? "600" : "400",
                        }}
                        onMouseOver={(e) => {
                          if (cat.slug !== slug) {
                            e.currentTarget.style.color = config.colors.primary;
                          }
                        }}
                        onMouseOut={(e) => {
                          if (cat.slug !== slug) {
                            e.currentTarget.style.color =
                              config.colors.textPrimary || "#6B7280";
                          }
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 mr-2"
                          style={{ color: config.colors.primary }}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={cat.slug === slug ? 2 : 0}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                        {cat.name}
                      </Link>
                    </li>
                  ))}
              </ul>
            </div>

            {/* Category Information */}
            {category.description && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h3
                  className="text-xl font-bold mb-4"
                  style={{ color: config.colors.textPrimary || "#111827" }}
                >
                  About This Category
                </h3>
                <p style={{ color: config.colors.textSecondary || "#6B7280" }}>
                  {category.description}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CSS for spinner and line clamp */}
      <style jsx>{`
        .spinner {
          border: 3px solid rgba(0, 0, 0, 0.1);
          border-radius: 50%;
          border-top: 3px solid ${config.colors.primary || "#4f46e5"};
          width: 24px;
          height: 24px;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default BlogCategory;

// //before coloring from configcontext
// // src/pages/blog/BlogCategory.js
// import React, { useState, useEffect } from 'react';
// import { useParams, Link, useNavigate } from 'react-router-dom';
// import { Search, Calendar, Eye, ChevronRight, AlertCircle, ArrowLeft } from 'lucide-react';
// import  Helmet from 'react-helmet';

// const BlogCategory = () => {
//   const { slug } = useParams();
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [category, setCategory] = useState(null);
//   const [categories, setCategories] = useState(null);
//   const [posts, setPosts] = useState([]);
//   const [totalPages, setTotalPages] = useState(1);
//   const [currentPage, setCurrentPage] = useState(1);
//   const navigate = useNavigate();

//   useEffect(() => {
//     fetchCategory();
//   }, [slug]);

//   useEffect(() => {
//     fetchCategories();
//   });

//   useEffect(() => {
//     if (category) {
//       fetchPosts(currentPage);
//     }
//   }, [category, currentPage]);

//   // Skeleton loader for loading state
//     const SkeletonCard = () => (
//     <div className="bg-white rounded-lg overflow-hidden shadow-md animate-pulse">
//       <div className="h-48 bg-gray-200"></div>
//       <div className="p-5">
//         <div className="flex gap-2 mb-3">
//           <div className="h-6 w-16 bg-gray-200 rounded-full"></div>
//           <div className="h-6 w-20 bg-gray-200 rounded-full"></div>
//         </div>
//         <div className="h-6 bg-gray-200 rounded mb-3"></div>
//         <div className="h-4 bg-gray-200 rounded mb-2"></div>
//         <div className="h-4 bg-gray-200 rounded mb-2"></div>
//         <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
//         <div className="h-6 w-24 bg-gray-200 rounded"></div>
//       </div>
//     </div>
//   );

//   const fetchCategory = async () => {
//     setLoading(true);
//     try {
//       const res = await fetch(`/api/blog/categories/${slug}`);

//       if (!res.ok) {
//         if (res.status === 404) {
//           throw new Error('Category not found');
//         }
//         throw new Error('Failed to fetch category');
//       }

//       const data = await res.json();
//       setCategory(data);
//       setError(null);
//     } catch (err) {
//       console.error('Error fetching category:', err);
//       setError(err.message || 'Failed to load the category');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const fetchCategories = async () => {
//     setLoading(true);
//     try {
//       const res = await fetch(`/api/blog/categories`);

//       if (!res.ok) {
//         if (res.status === 404) {
//           throw new Error('Category not found');
//         }
//         throw new Error('Failed to fetch category');
//       }

//       const data = await res.json();
//       setCategories(data);
//       setError(null);
//     } catch (err) {
//       console.error('Error fetching category:', err);
//       setError(err.message || 'Failed to load the category');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const fetchPosts = async (page) => {
//     setLoading(true);
//     try {
//       // Build query params
//       const params = new URLSearchParams({
//         page,
//         limit: 6,
//         sort: 'newest'
//       });

//       const res = await fetch(`/api/blog/posts?category=${slug}&${params.toString()}`);

//       if (!res.ok) throw new Error('Failed to fetch posts');

//       const data = await res.json();
//       setPosts(data.posts);
//       setTotalPages(data.totalPages);

//       setError(null);
//     } catch (err) {
//       console.error('Error fetching posts:', err);
//       setError('Failed to load posts. Please try again.');
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Format date
//   const formatDate = (dateString) => {
//     const options = { year: 'numeric', month: 'long', day: 'numeric' };
//     return new Date(dateString).toLocaleDateString(undefined, options);
//   };

//   // Calculate read time in minutes
//   const getReadTime = (content) => {
//     const wordsPerMinute = 200;
//     const wordCount = content.split(/\s+/).length;
//     return Math.ceil(wordCount / wordsPerMinute);
//   };

//   if (loading && !category) {
//     return (
//       <div className="bg-gray-50 min-h-screen">
//         <div className="container mx-auto px-4 py-12">
//           <div className="animate-pulse">
//             <div className="h-12 bg-gray-200 rounded w-1/3 mx-auto mb-8"></div>
//             <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto mb-12"></div>

//             <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
//               <SkeletonCard />
//               <SkeletonCard />
//               <SkeletonCard />
//               <SkeletonCard />
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   if (error || !category) {
//     return (
//       <div className="bg-gray-50 min-h-screen">
//         <div className="container mx-auto px-4 py-8">
//           <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
//             <p>{error || 'Category not found'}</p>
//             <button
//               onClick={() => navigate('/blog')}
//               className="text-red-700 font-bold hover:underline mt-2 inline-block"
//             >
//               Return to Blog
//             </button>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   return (
//         <div className="min-h-screen bg-gray-50">
//           <Helmet>
//             <title>{category.name} Category | BagBox</title>
//             <meta name="description" content={`Articles in the ${category.name} category - Bag&Box Blog`} />
//           </Helmet>

//           {/* Category Header */}
//           <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-16">
//             <div className="container mx-auto px-4">
//               <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center">{category.name}</h1>
//               {category.description && (
//                 <p className="text-xl text-center max-w-2xl mx-auto">
//                   {category.description}
//                 </p>
//               )}

//               {/* Breadcrumbs */}
//               <div className="flex justify-center mt-6 text-blue-100">
//                 <nav className="flex items-center">
//                   <Link to="/" className="hover:text-white">Home</Link>
//                   <span className="mx-2">/</span>
//                   <Link to="/blog" className="hover:text-white">Blog</Link>
//                   <span className="mx-2">/</span>
//                   <span>Category</span>
//                   <span className="mx-2">/</span>
//                   <span className="font-medium">{category.name}</span>
//                 </nav>
//               </div>
//             </div>
//           </div>

//           {/* Main content container */}
//           <div className="container mx-auto px-4 py-12">
//             <div className="flex flex-col lg:flex-row gap-8">
//               {/* Posts section - takes up 3/4 of the width on large screens */}
//               <div className="w-full lg:w-3/4">
//                 {error && (
//                   <div className="bg-red-100 text-red-800 p-4 rounded-lg mb-8">
//                     <div className="flex items-center space-x-2">
//                       <AlertCircle className="h-5 w-5" />
//                       <span>{error}</span>
//                     </div>
//                   </div>
//                 )}

//                 {/* Posts Grid */}
//                 {loading && !posts.length ? (
//                   <div className="text-center py-20">
//                     <div className="spinner mx-auto mb-4"></div>
//                     <p className="text-gray-600">Loading posts...</p>
//                   </div>
//                 ) : posts.length === 0 ? (
//                   <div className="text-center py-20 bg-gray-50 rounded-lg">
//                     <p className="text-gray-500 mb-4">No posts found in this category</p>
//                     <Link
//                       to="/blog"
//                       className="text-blue-600 hover:text-blue-800"
//                     >
//                       Back to Blog
//                     </Link>
//                   </div>
//                 ) : (
//                   <>
//                     <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
//                       {posts.map(post => (
//                         <div key={post._id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
//                           <Link to={`/blog/${post.slug}`}>
//                             <div className="h-48 bg-gray-200">
//                               {post.coverImage ? (
//                                 <img
//                                   src={post.coverImage}
//                                   alt={post.title}
//                                   className="w-full h-full object-cover"
//                                 />
//                               ) : (
//                                 <div className="w-full h-full flex items-center justify-center">
//                                   <span className="text-gray-400">No image</span>
//                                 </div>
//                               )}
//                             </div>
//                           </Link>
//                           <div className="p-6">
//                             <div className="flex items-center mb-2 text-sm text-gray-500">
//                               <Calendar className="h-4 w-4 mr-1" />
//                               {formatDate(post.publishedAt || post.createdAt)}
//                               <span className="mx-2">•</span>
//                               <Eye className="h-4 w-4 mr-1" />
//                               {post.viewCount} views
//                             </div>
//                             <h3 className="text-xl font-bold mb-2 hover:text-blue-600 line-clamp-2">
//                               <Link to={`/blog/${post.slug}`}>
//                                 {post.title}
//                               </Link>
//                             </h3>
//                             <p className="text-gray-600 mb-4 line-clamp-3">
//                               {post.excerpt}
//                             </p>
//                             <Link
//                               to={`/blog/${post.slug}`}
//                               className="text-blue-600 hover:text-blue-800 flex items-center text-sm font-medium"
//                             >
//                               Read More
//                               <ChevronRight className="h-4 w-4 ml-1" />
//                             </Link>
//                           </div>
//                         </div>
//                       ))}
//                     </div>

//                     {/* Pagination */}
//                     {totalPages > 1 && (
//                       <div className="mt-10 flex justify-center">
//                         <div className="flex items-center space-x-2">
//                           <button
//                             onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
//                             disabled={currentPage === 1}
//                             className={`px-3 py-1 rounded ${
//                               currentPage === 1
//                                 ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
//                                 : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
//                             }`}
//                           >
//                             Previous
//                           </button>

//                           {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
//                             <button
//                               key={page}
//                               onClick={() => setCurrentPage(page)}
//                               className={`w-8 h-8 rounded flex items-center justify-center ${
//                                 currentPage === page
//                                   ? 'bg-blue-600 text-white'
//                                   : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
//                               }`}
//                             >
//                               {page}
//                             </button>
//                           ))}

//                           <button
//                             onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
//                             disabled={currentPage === totalPages}
//                             className={`px-3 py-1 rounded ${
//                               currentPage === totalPages
//                                 ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
//                                 : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
//                             }`}
//                           >
//                             Next
//                           </button>
//                         </div>
//                       </div>
//                     )}
//                   </>
//                 )}
//               </div>

//               {/* Sidebar - takes up 1/4 of the width on large screens */}
//               <div className="w-full lg:w-1/4 mt-8 lg:mt-0">
//                 {/* Categories */}
//                 <div className="bg-white rounded-lg shadow-md p-6 mb-6">
//                   <h3 className="text-xl font-bold text-gray-800 mb-4">Categories</h3>
//                   <ul className="space-y-2">
//                     {categories && categories.map((cat) => (
//                       <li key={cat.id} className="group">
//                         <Link
//                           to={`/blog/category/${cat.slug}`}
//                           className={`${
//                             cat.slug === slug ? 'text-blue-600 font-semibold' : 'text-gray-700 hover:text-blue-600'
//                           } flex items-center group-hover:translate-x-1 transition-transform`}
//                         >
//                           <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={cat.slug === slug ? 2 : 0} d="M9 5l7 7-7 7" />
//                           </svg>
//                           {cat.name}
//                         </Link>
//                       </li>
//                     ))}
//                   </ul>
//                 </div>

//                 {/* Category Information */}
//                 {category.description && (
//                   <div className="bg-white rounded-lg shadow-md p-6 mb-6">
//                     <h3 className="text-xl font-bold text-gray-800 mb-4">About This Category</h3>
//                     <p className="text-gray-700">{category.description}</p>
//                   </div>
//                 )}

//                 {/* Subscribe Box */}
//                 {/* <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-md p-6 text-white">
//                   <h3 className="text-xl font-bold mb-4">Subscribe</h3>
//                   <p className="mb-4 text-blue-100">Get updates on {category.name} articles</p>
//                   <form className="space-y-4">
//                     <input
//                       type="email"
//                       placeholder="Your email address"
//                       className="w-full px-4 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-white text-gray-800"
//                       required
//                     />
//                     <button
//                       type="submit"
//                       className="w-full bg-white text-blue-600 hover:bg-blue-50 font-medium py-2 px-4 rounded-md transition duration-300"
//                     >
//                       Subscribe
//                     </button>
//                   </form>
//                 </div> */}
//               </div>
//             </div>
//           </div>

//             {/* CSS for spinner and line clamp */}
//             <style jsx>{`
//                 .spinner {
//                 border: 3px solid rgba(0, 0, 0, 0.1);
//                 border-radius: 50%;
//                 border-top: 3px solid #4f46e5;
//                 width: 24px;
//                 height: 24px;
//                 animation: spin 1s linear infinite;
//                 }

//                 @keyframes spin {
//                 0% { transform: rotate(0deg); }
//                 100% { transform: rotate(360deg); }
//                 }

//                 .line-clamp-2 {
//                 display: -webkit-box;
//                 -webkit-line-clamp: 2;
//                 -webkit-box-orient: vertical;
//                 overflow: hidden;
//                 }

//                 .line-clamp-3 {
//                 display: -webkit-box;
//                 -webkit-line-clamp: 3;
//                 -webkit-box-orient: vertical;
//                 overflow: hidden;
//                 }
//             `}</style>
//         </div>
//   );
// };

// export default BlogCategory;
